import * as React from 'react';
import { useEffect, useMemo } from 'react';
import Modal from '../../../layout/modal/Modal';
import ModalHeader from '../../../layout/modal/ModalHeader';
import { useMsgText } from '../../../localization/hooks';
import { Str } from '../../../localization/translationKeys';
import LocalDiv from '../../../localization/LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../../../layout/flexBox';
import LocalBtn from '../../../localization/LocalBtn';
import { COLORS } from '../../../globalStyle/colors';
import { btnStyle } from '../../../forms/styles/button';
import { css } from '@emotion/core';
import LocalTextArea from '../../../localization/LocalTextArea';
import Spacer from '../../../layout/Spacer';
import { useFormik } from 'formik';
import { customizeMessageFormValidation } from './formValidations';
import { EmailBody } from './models';
import { onMobileMedium, onPhone } from '../../../globalStyle/mediaQueries';
import { FIELDSET_DEFAULT } from '../../../layout/fieldSetStyle';
import { useProjIds, useRaterApprovalIds } from '../../../navigation/hooks';
import { useSessionStorage } from '../../../core/hooks/useSessionStorage';
import { SessionStorageKeys } from '../../../localStorage/sessionStorageKeys';
import { useMount } from '../../../core/hooks/useMount';
import { usePortalType } from '../../../auth/hooks';
import { PortalType } from '../../../navigation/portalType';
import { useAppState } from '../../../core/redux/hooks';
import { participantByUniqueIdentifier } from '../../../raterApproval/module';


const styles = {
  root:             css`
    min-width: 780px;

    @media only screen and (max-width: 812px) {
      min-width: calc(100vw - 32px);
    }

    ${onMobileMedium} {
      max-width: 100vw;
      max-height: 100vh;
      min-width: 0;
      min-height: 0;
      width: 100vw;
      height: 100vh;
    }
  `,
  contentContainer: css`
    margin: 18px;

    ${onMobileMedium} {
      margin: 12px;
    }
  `,
  content:          css`
    justify-content: center;
    align-items: stretch;
    margin: auto 42px;

    ${onMobileMedium} {
      margin: auto 12px;
    }

    ${onPhone} {
      margin: 0;
    }
  `,
  textArea:         css`
    padding: 8px;
    border: 0 none;
    resize: none;
    font-size: 12px;
  `,
  previewContainer: css`
    margin-top: 30px;
    margin-bottom: 45px;

    ${onMobileMedium} {
    margin-top: 15px;
    margin-bottom: 15px;
    }
  `,
  emailPreview:     css`
    margin-top: auto;
    background-color: ${COLORS.WHITE};
    border: 0 none;
    min-height: 300px;
    width: 100%;

    ${onMobileMedium} {
      min-height: calc(100vh - 270px);
    }
  `,
  btnSpacer:        css`
    ${onPhone} {
      display: none;
    }
  `,
  button:           css`
    min-height: 35px;
    min-width: 132px;
  `,
};


type Props = {
  modalTitle: Str;

  emailBody?: EmailBody;
  emailComment: string;
  setEmailComment: (comment: string) => void;
  ieIframeRoute: string;

  dismiss: () => void;
  className?: string;

  emailLogo: string;
  participant: string;
  survey: string;
  dateDue: string;
  ratersApproved: string;
  ratersDeclined: string;
  ratersAdded: string;
};


/**
 * CustomizeEmailModal: a component that displays an HTML preview of the provided email and (except in IE) the custom
 * comment text added to that message by the user.
 *
 * This comment is stored until the users navigates away from the invite-raters view.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CustomizeEmailModal: React.FC<Props> = props => {
  const portalType = usePortalType();

  const portalProjectId = useProjIds().projId;
  
  const participantId = useRaterApprovalIds().participantId;
  const projDisplayId = useRaterApprovalIds().projDisplayId;
  const participants = useAppState(state => state.raterApprovalParticipants ?? []);
  const participant = useMemo(
    () => participantByUniqueIdentifier(`${(projDisplayId)}|${(participantId)}`, participants),
    [participants, participantId, projDisplayId],
  );
  const raterApprovalProjectId = (participant?.projectId ?? 0) > 0 ? participant?.projectId : 0;

  const projectId = portalType === PortalType.RATER_APPROVAL ? raterApprovalProjectId : portalProjectId;

  const [, setCustomEmailTxt] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailTxt}-${projectId}`,
  ); //<@ExtraText>
  const [, setCustomEmailLogo] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailLogo}-${projectId}`,
  );  //<@PrimaryLogo>
  const [, setCustomEmailParticipant] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailParticipant}-${projectId}`,
  ); //<@cRateeName>
  const [, setCustomEmailSurvey] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailSurvey}-${projectId}`,
  ); //<@cSurveyName>
  const [, setCustomEmailDateDue] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailDateDue}-${projectId}`,
  ); //<@cDateDueToBooth>
  const [, setCustomEmailRatersApproved] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailRatersApproved}-${projectId}`,
  ); //<@cRatersApproved>
  const [, setCustomEmailRatersDeclined] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailRatersDeclined}-${projectId}`,
  ); //<@cRatersDeclined>
  const [, setCustomEmailRatersAdded] = useSessionStorage<string | undefined>(
    `${SessionStorageKeys.CustomEmailRatersAdded}-${projectId}`,
  ); //<@cRatersAddedBySupervisor>

  useMount(() => {
    setCustomEmailLogo(props.emailLogo);
    setCustomEmailParticipant(props.participant);
    setCustomEmailSurvey(props.survey);
    setCustomEmailDateDue(props.dateDue);
    setCustomEmailRatersApproved(props.ratersApproved);
    setCustomEmailRatersDeclined(props.ratersDeclined);
    setCustomEmailRatersAdded(props.ratersAdded);
  });

  const title           = useMsgText(props.modalTitle);
  const previewBoxTitle = useMsgText(Str.EmailPreview);

  const { handleSubmit, handleChange, resetForm, values } = useFormik({
    initialValues: {
      customText: props.emailComment,
    },

    onSubmit: (values) => {
      props.setEmailComment(values.customText);
      props.dismiss();
      resetForm();
    },

    validationSchema:   customizeMessageFormValidation,
    validateOnChange:   false,
    enableReinitialize: true,
  });

  useEffect(
    () => {
      // Set dirty comment state into session storage for iFrame to read out
      setCustomEmailTxt(values.customText.replace(/\n/g, '<br/>'));

      // Return cleanup function to clear out dirty state onUnmount
      return () => setCustomEmailTxt(undefined);
    },
    [values.customText, setCustomEmailTxt],
  );

  return (
    <Modal css={styles.root} className={props.className} dismissOnClickOutside={false} onDismiss={props.dismiss}>
      <ModalHeader title={title} onDismiss={props.dismiss} />
      <fieldset css={[FIELDSET_DEFAULT, FLEX_COLUMN, styles.contentContainer]}>
        <form onSubmit={handleSubmit}>
          <LocalDiv css={[FLEX_COLUMN, styles.content]}>
            <LocalTextArea
              name='customText'
              css={[
                styles.textArea,
                css`color: ${!!values.customText ? COLORS.DARK_GREY : COLORS.FIELD_GREY}`,
              ]}
              rows={4}
              placeholderTranslation={Str.AddOptionalText}
              value={values.customText}
              onChange={handleChange}
            />
            <LocalDiv css={[FLEX_COLUMN, styles.previewContainer]}>
              <LocalDiv css={css`margin-bottom: 8px;`}>
                {previewBoxTitle}
              </LocalDiv>
              <iframe
                allowFullScreen={true}
                css={styles.emailPreview}
                title='invite-template-body'
                src={props.ieIframeRoute + `#${projectId}`}
              />
            </LocalDiv>
          </LocalDiv>
          <LocalDiv css={FLEX_ROW}>
            <div css={[css`flex-basis: 100%;`, styles.btnSpacer]} />
            <LocalBtn
              str={Str.Cancel}
              onClick={props.dismiss}
              css={[btnStyle.transparent(COLORS.BLUE), styles.button]}
            />
            <Spacer sizePx={10} />
            <LocalBtn
              type='submit'
              disabled={!values.customText}
              str={Str.Save}
              css={[btnStyle.primary(COLORS.BLUE), styles.button]}
            />
          </LocalDiv>
        </form>
      </fieldset>
    </Modal>
  );
};

export default CustomizeEmailModal;
